import * as React from 'react';
import { VoiceAgentDialog } from '@/components/ui/voice-agent-dialog';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { useAuth } from '@/hooks/useAuth';
import { useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { useWeb3Auth } from '@/providers/Web3AuthProvider';

export function GenUI() {
	const [isDialogOpen, setIsDialogOpen] = React.useState(false);
	const { user: authUser, isLoading } = useAuth();
	const { walletAddress } = useWeb3Auth();

	const userData = useQuery(
		api.users.getByWallet,
		!isLoading && walletAddress ? { walletAddress: walletAddress } : 'skip'
	);

	// Debug logging for auth and data flow
	React.useEffect(() => {
		console.log('🔐 [GenUI] Auth State:', {
			authUserId: authUser?.id,
			walletAddress: walletAddress,
			email: authUser?.email,
			hasUser: !!userData,
			isLoading,
			timestamp: new Date().toISOString(),
		});

		if (isLoading) {
			console.log('⏳ [GenUI] Auth loading...');
			return;
		}

		if (!walletAddress) {
			console.log('⚠️ [GenUI] No wallet address available');
			return;
		}

		if (userData === undefined) {
			console.log('⏳ [GenUI] Convex query still loading...');
		} else if (userData === null) {
			console.log('❌ [GenUI] User not found in database');
		} else {
			console.log('📦 [GenUI] User Data Received:', {
				id: userData._id,
				email: userData.email,
				name: userData.name,
				role: userData.role,
				isAdmin: userData.isAdmin,
				timestamp: new Date().toISOString(),
			});
		}
	}, [authUser?.id, walletAddress, authUser?.email, userData, isLoading]);

	// Prepare user data for voice agent
	const userDataForVoiceAgent = React.useMemo(() => {
		if (!userData) {
			console.log('⚠️ [GenUI] No user data available for voice agent');
			return undefined;
		}

		const preparedData = {
			email: userData.email,
			name: userData.name,
			role: userData.role,
			isAdmin: userData.isAdmin,
		};

		console.log('✅ [GenUI] Prepared Voice Agent User Data:', {
			data: preparedData,
			timestamp: new Date().toISOString(),
		});
		return preparedData;
	}, [userData]);

	return (
		<div className='container mx-auto p-6 pb-80 relative'>
			<div className='flex justify-between items-center mb-6'>
				<div>
					<h1 className='text-3xl font-bold py-4'>
						Prometheus Generative UI
					</h1>
					<p className='text-lg text-gray-500 -mt-2 mb-4'>
						Experimental Generative Voice Agent Interface
					</p>
				</div>
			</div>

			{isLoading ? (
				<p>Loading user data...</p>
			) : (
				<>
					<div className='flex flex-col items-center justify-center mt-20'>
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5 }}
						>
							<Button
								onClick={() => setIsDialogOpen(true)}
								className='bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white px-8 py-6 rounded-xl text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105'
								disabled={!userData}
							>
								<span className='mr-2'>🎙️</span>
								Launch Voice Agent
							</Button>
						</motion.div>
					</div>

					{isDialogOpen && (
						<VoiceAgentDialog
							isOpen={isDialogOpen}
							onClose={() => setIsDialogOpen(false)}
							userData={userDataForVoiceAgent}
						/>
					)}
				</>
			)}
		</div>
	);
}
